import { Card, CardButtonActionMode, HeaderGradient, MiniCard, Row } from '@app/components';
import React, { memo } from 'react';
import { theme } from '@app/theme';
import { useDynamicSizes } from '@app/hooks';
import LinearGradient from 'react-native-linear-gradient';
import { StyleSheet } from 'react-native';
import { IOption } from '@app/common';
import { useAppSelector } from '@app/store/hooks';
import { triptychSelectors } from '@app/store';
import SvgInfo from '@app/components/icons/SvgInfo';
import { SlantTitle } from '@app/components/typography';

type OptionCardProps = {
  option?: Partial<IOption>;
  toggle: (mode: CardButtonActionMode) => void;
};

const formatCardData = (partialOption: Partial<IOption>) => {
  return {
    name: partialOption?.name || 'Nouvelle option'
  }
}

export const OptionCard = memo(({ option, toggle }: OptionCardProps) => {
  const { subContainerHeight } = useDynamicSizes();

  const partialOption = useAppSelector<Partial<IOption>>(triptychSelectors.selectData);
  const cardData = useAppSelector<Partial<IOption>>(triptychSelectors.selectCardData);

  const { name } = formatCardData(cardData ?? partialOption);

  return (
    <Card>

      <HeaderGradient>
        <LinearGradient
          start={{ x: 0.5, y: 0 }}
          end={{ x: 0.5, y: 1 }}
          locations={[0, 1]}
          colors={['rgba(253, 255, 252, 0)', 'rgba(253, 255, 252, 1)']}
          style={styles.headerGradient}
        />
      </HeaderGradient>

      <Card.TopHeader>
        <Card.TopHeaderLabel>Fiche option</Card.TopHeaderLabel>
      </Card.TopHeader>

      <Card.Container height={subContainerHeight}>
        <Card.Background source={require('@app/assets/img/cardbackground.png')} />

        <Card.Content>
          <Card.Header>
            {/* En-tête */}
            <Row justify="space-between" align="center">
              <Card.HeaderTitles>
                <Card.Title numberOfLines={2}>{name}</Card.Title>
                {option?._id && <Card.Subtitle>Ref: {option._id}</Card.Subtitle>}
              </Card.HeaderTitles>

              <Card.ButtonAction onPress={toggle} permissionScope="options" />
            </Row>
          </Card.Header>
        </Card.Content>

        <MiniCard>
          <MiniCard.Tabs>
            <MiniCard.TabsHolder>
              <MiniCard.Tab Icon={SvgInfo} isActive />
            </MiniCard.TabsHolder>
          </MiniCard.Tabs>
          <MiniCard.Container>
            <MiniCard.Content>

              <SlantTitle label="Aide" color={theme.colors.gold} />

              <Card.HelpScrollContainer>

                <Card.LineBreak height={16} />

                <Card.HelpTitle>Fonctionnement des options</Card.HelpTitle>

                <Card.LineBreak height={8} />

                <Card.HelpText>
                  Une option est composée uniquement de produits, et non d’ingrédients.
                  Il est donc nécessaire de créer d’abord les produits qui seront utilisés dans l’option.
                  L’option prend le prix du produit associé.
                </Card.HelpText>

                <Card.LineBreak height={8} />

                <Card.HelpText>Exemples : </Card.HelpText>
                <Card.HelpExample>
                  Une pâte classique à 0 € ⇒ option gratuite{"\n"}
                  Une pâte pan à 2 € ⇒ option facturée 2 €
                </Card.HelpExample>

                <Card.LineBreak height={8} />

                <Card.HelpText>
                  Les produits utilisés dans les options doivent conserver les mêmes déclinaisons que les produits auxquels ils sont associés.
                </Card.HelpText>

                <Card.LineBreak height={8} />

                <Card.HelpText>Exemple : </Card.HelpText>

                <Card.HelpExample>
                  Une pizza Médium avec comme option supplément poivron — le produit « Poivron » doit également avoir la variation Médium.
                </Card.HelpExample>

                <Card.LineBreak height={8} />

                <Card.HelpExample>
                  Cela vous permet de faire varier le prix du supplément en fonction de la taille.
                </Card.HelpExample>

                <Card.LineBreak height={16} />

                <Card.HelpText>
                  Pour suivre le tutoriel complet, rendez-vous{" "}
                  <Card.HelpLink href="https://tickeat.featurebase.app/en/help/articles/6858858-creer-et-attribuer-une-option-a-un-produit">
                    ici
                  </Card.HelpLink>
                </Card.HelpText>

                <Card.LineBreak height={75} />

              </Card.HelpScrollContainer>
            </MiniCard.Content>
          </MiniCard.Container>
        </MiniCard>

      </Card.Container>
    </Card>
  );
});


const styles = StyleSheet.create({
  headerGradient: {
    flex: 1,
    height: theme.sizes.listHeaderHeight,
  },
});
